/// <reference types="vite/client" />

import React from 'react';
import ReactDOM from 'react-dom/client';

import './index.css';
import App from './App.jsx';
import logger from './utils/logger';
import { initSentry } from './utils/sentry';
import { QueryClientProvider } from './config/reactQueryConfig.jsx';
import serviceWorkerManager from './services/serviceWorkerManager';
import indexedDBService from './services/indexedDBService';
// @VH_DELETE_CANDIDATE: Offline queue removed - vh-web is online-only | delete_after=2026-02-15
// import offlineQueueService from './services/offlineQueueService';
import predictivePreloadService from './services/predictivePreloadService';
import { performanceMonitor } from './services/performanceMonitoring';

// Type definitions for service worker and offline services
interface ServiceWorkerManager {
  registerServiceWorker(): Promise<void>;
  onUpdateAvailable(callback: (newWorker: any) => void): void;
}

interface IndexedDBService {
  isSupported: boolean;
  openDatabase(): Promise<void>;
  cleanExpiredCache(): Promise<void>;
}

interface OfflineQueueService {
  syncQueue(): Promise<void>;
}

interface PredictivePreloadService {
  // Add interface properties as needed
}

// Add error logging
console.log('🚀 Starting Virtual Hardhat...');
console.log('✅ React:', typeof React);
console.log('✅ ReactDOM:', typeof ReactDOM);
console.log('✅ App:', typeof App);
console.log('✅ Logger:', typeof logger);

// Global error handler to catch any unhandled errors
window.addEventListener('error', (event) => {
  console.error('❌ GLOBAL ERROR:', event.error);
  document.body.innerHTML = `
    <div style="padding: 20px; font-family: monospace; background: #fee; color: #c00;">
      <h1>Error Loading App</h1>
      <p><strong>Message:</strong> ${event.error?.message || event.message}</p>
      <pre>${event.error?.stack || 'No stack trace'}</pre>
    </div>
  `;
});

// Initialize offline-first services
async function initializeOfflineServices(): Promise<void> {
  try {
    // Register service worker
    await serviceWorkerManager.registerServiceWorker();

    // Listen for service worker updates
    serviceWorkerManager.onUpdateAvailable((newWorker: any) => {
      logger.info('Service worker update available');
      // Show update notification (could be implemented with a toast)
    });

    // Initialize IndexedDB
    if (indexedDBService.isSupported) {
      await indexedDBService.openDatabase();
      logger.info('✅ IndexedDB initialized');

      // Clean up expired cache entries periodically
      setInterval(() => {
        indexedDBService.cleanExpiredCache().catch((error: Error) => {
          logger.debug('Failed to clean expired cache:', error.message);
        });
      }, 5 * 60 * 1000); // Every 5 minutes
    }

    // @VH_DELETE_CANDIDATE: Offline queue sync removed - vh-web is online-only | delete_after=2026-02-15
    // Online/offline sync triggers removed - vh-web is online-only
    // Offline queues belong in vh-mobile only

    logger.info('✅ Online-only services initialized');
  } catch (error) {
    logger.error('Failed to initialize offline services:', error);
  }
}

// Initialize Sentry monitoring (lazy loaded)
initSentry().catch((error: Error) => {
  logger.warn('Sentry initialization failed', { error });
});

try {
  const rootElement = document.getElementById('root') as HTMLElement | null;
  logger.info('✅ Root element:', rootElement);

  if (!rootElement) {
    throw new Error('Root element not found');
  }

  const root = ReactDOM.createRoot(rootElement);
  logger.info('✅ React root created');

  root.render(
    <React.StrictMode>
      <QueryClientProvider>
        <App />
      </QueryClientProvider>
    </React.StrictMode>
  );
  logger.info('✅ App rendered');

  // Start Core Web Vitals monitoring in production (opt-in)
  const enableWebVitals = import.meta.env.VITE_ENABLE_WEB_VITALS === 'true';
  if (import.meta.env.PROD && enableWebVitals) {
    performanceMonitor.start();
  }

  // Development mode logging (optional, for debugging)
  if (import.meta.env.DEV) {
    import('./reportWebVitals').then(({ default: reportWebVitals }) => {
      reportWebVitals(console.log);
    });
  }

  // Initialize offline-first services
  initializeOfflineServices().catch((error: Error) => {
    logger.error('❌ Failed to initialize offline services:', error);
  });
} catch (error) {
  logger.error('❌ Fatal error during app initialization:', { error });

  const rootElement = document.getElementById('root');
  if (rootElement) {
    rootElement.innerHTML = `
      <div style="padding: 20px; font-family: monospace; background: #fee; color: #c00;">
        <h1>Fatal Error</h1>
        <p>${(error as Error).message}</p>
        <pre>${(error as Error).stack}</pre>
      </div>
    `;
  }
}
